interface Card {
  number: string;
  title: string;
  description: string;
  icon: string;
  detail: string;
  imageUrl: string;
}

const cards: Card[] = [
  {
    number: '01',
    title: 'Capture',
    description:
      'Automatically collect and organize inbound leads from every channel—web, chat, phone, and CRM—into one structured pipeline.',
    icon: 'ri-inbox-line',
    detail: 'Web forms · Chat · Phone · CRM sync',
    imageUrl:
      'https://public.readdy.ai/ai/img_res/e73b68aa4a392e877fcde8c792c3eb87.jpg',
  },
  {
    number: '02',
    title: 'Qualify',
    description:
      'AI assistants engage, respond, filter, and prioritize leads in real time—so your team only talks to prospects who are ready.',
    icon: 'ri-robot-line',
    detail: 'Instant response · Lead scoring · Smart routing',
    imageUrl:
      'https://readdy.ai/api/search-image?query=abstract%20AI%20neural%20network%20nodes%20scoring%20ranking%20priority%20visualization%20minimal%20clean%20dark%20blue%20background%20glowing%20light%20elegant%20technology%20enterprise%20graphic&width=600&height=420&seq=henexa-card-qualify-v2&orientation=landscape',
  },
  {
    number: '03',
    title: 'Convert',
    description:
      'Seamless booking, automated follow-ups, and pipeline progression—ensuring no qualified lead is ever left waiting.',
    icon: 'ri-calendar-check-line',
    detail: 'Auto-scheduling · Follow-up sequences · CRM update',
    imageUrl:
      'https://readdy.ai/api/search-image?query=clean%20calendar%20booking%20scheduling%20interface%20minimal%20white%20dashboard%20confirmed%20checkmark%20success%20green%20glow%20enterprise%20professional%20software%20visualization%20modern%20ui&width=600&height=420&seq=henexa-card-convert-v2&orientation=landscape',
  },
];

export default function SystemOverview() {
  return (
    <section id="system-overview" className="bg-white dark:bg-[#070D1B]">
      <div className="max-w-7xl mx-auto px-8 lg:px-16 pt-28 pb-20">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-20">
          <div>
            <span className="text-xs tracking-widest uppercase text-[#2563EB] font-medium block mb-5">
              System Overview
            </span>
            <h2 className="text-5xl lg:text-6xl font-semibold text-[#0B2A4A] dark:text-white tracking-[-0.025em] leading-[1.0]">
              Three steps.<br />One structured system.
            </h2>
          </div>
          <p className="text-sm text-[#0B2A4A]/65 dark:text-white/55 max-w-xs tracking-wide leading-relaxed lg:text-right">
            Every component designed to work together, eliminating the gaps where leads get lost.
          </p>
        </div>

        {/* Visual cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {cards.map((card) => (
            <div
              key={card.number}
              className="group rounded-2xl overflow-hidden border border-[#E5E7EB] dark:border-white/10 hover:border-[#2563EB]/30 dark:hover:border-[#2563EB]/40 transition-all duration-300 bg-white dark:bg-[#0F1928] flex flex-col"
            >
              {/* Image area */}
              <div className="relative h-52 overflow-hidden bg-[#F8FAFC] dark:bg-[#0C1322]">
                <img
                  src={card.imageUrl}
                  alt={card.title}
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                />
                {/* Top accent line on hover */}
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-[#2563EB] scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
              </div>

              {/* Content area */}
              <div className="p-8 flex flex-col flex-1">
                <div className="flex items-start justify-between mb-6">
                  <span className="text-xs text-[#0B2A4A]/55 dark:text-white/40 font-medium tracking-widest">
                    {card.number}
                  </span>
                  <div className="w-9 h-9 flex items-center justify-center bg-[#F8FAFC] dark:bg-[#0C1322] rounded-lg border border-[#E5E7EB] dark:border-white/10 group-hover:bg-[#2563EB]/8 group-hover:border-[#2563EB]/20 transition-colors">
                    <i className={`${card.icon} text-[#2563EB] text-base`} />
                  </div>
                </div>

                <div className="w-6 h-0.5 bg-[#2563EB] mb-5 group-hover:w-10 transition-all duration-300" />
                <h3 className="text-xl font-semibold text-[#0B2A4A] dark:text-white tracking-tight mb-3">{card.title}</h3>
                <p className="text-sm text-[#0B2A4A]/70 dark:text-white/60 leading-relaxed tracking-wide mb-6 flex-1">
                  {card.description}
                </p>
                <p className="text-xs text-[#0B2A4A]/55 dark:text-white/40 tracking-widest uppercase font-medium border-t border-[#E5E7EB] dark:border-white/10 pt-4">
                  {card.detail}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
